import type { DrawingIssueAnchor, IssueStatus, IssueViewpoint, PMIssue } from "@/domains/pm/types";
import type { StagedAttachment } from "@/domains/pm/components/issues/IssueAttachmentsStager";
import * as pmRepo from "@/stores/pm/repository";
import { track } from "@/lib/posthog";

/** WHA-3521: when the dialog is opened from a 3D element, pre-fill the project
 * from the owning IFC doc and attach the element to the new issue on save. */
export interface PrefilledElement {
  anchor_id: number;
  element_guid: string;
  element_name: string | null;
  element_type: string;
  /** id of the IFC document that owns the clicked element — recorded on the
   * link and also attached to the issue as a linked document. */
  ifc_document_id: number;
  /** The project the issue is filed in: the IFC document's that owns the
   *  clicked element, or the viewer's scoped project for a model in none. */
  ifc_project_id: number | null;
  /** The 3D view at the moment the dialog was opened, when a section cut was
   * live. Captured by IfcViewer — this dialog is modal, so it can neither see
   * the canvas nor reach the viewer handle. Null when no cut was active, which
   * is also what hides the "save the view" toggle. */
  viewpoint?: IssueViewpoint | null;
}

/** Exact absolute DWG location captured before the create dialog opens. */
export interface PrefilledDrawingAnchor {
  document_id: number;
  x: number;
  y: number;
}

/** A retry key: `{ current }`, so a React ref and a plain object both fit. */
export interface RetryKey<T> {
  current: T;
}

/** The one store call creation needs — structural, so the store can pass itself. */
export interface IssueCreator {
  createIssue: (
    data: Partial<PMIssue>,
    drawingAnchor?: Pick<DrawingIssueAnchor, "document_id" | "x" | "y">
  ) => Promise<PMIssue>;
}

export interface CreateIssueWithAssociationsParams {
  issuesStore: IssueCreator;
  buildingId: number;
  /** organisations.id is a varchar. */
  orgId?: string;
  userProfileId?: number;
  /** Already trimmed and non-empty — `pm_issues.title` is what the list shows. */
  title: string;
  status: IssueStatus;
  /** Empty string = unset; the writes below normalise it to null. */
  startDate: string;
  dueDate: string;
  projectId: string;
  assigneeIds: number[];
  disciplineIds: number[];
  parentIssueId: number | null;
  stagedAttachments: StagedAttachment[];
  checklistItems?: string[];
  /** The issue the checklist was already written to — a retry skips it. */
  checklistSavedForRef?: RetryKey<number | null>;
  prefilledElement?: PrefilledElement;
  prefilledDrawingAnchor?: PrefilledDrawingAnchor;
  saveViewpoint: boolean;
  /** Holds the id of an issue that was created but whose follow-up association
   * writes failed, so a retry reuses it instead of creating a duplicate. */
  createdIssueIdRef: RetryKey<number | null>;
  /** Surfaced only for the viewpoint save — the one opt-in the user ticked. */
  onViewpointSaveFailed: (err: unknown) => void;
  /** The inserted row, once — not on a retry that reuses the issue. */
  onIssueCreated?: (row: PMIssue) => void;
  /** Analytics `source` when neither an element nor a drawing anchor names one. */
  analyticsSource?: string;
}

/**
 * Create an issue plus every association the New issue form owns — parent,
 * disciplines, checklist, assignees, staged documents, BIM element — and return
 * its id. Shared by the dialog (through `submitIssueForm`) and the /issues
 * draft (through `IssuesStore.commitDraft`), so the two can never file an issue
 * differently.
 *
 * The ordering is the part worth reading: the id is recorded before any
 * association is written, and every association is an idempotent differential
 * write, so a failure part-way is retried against the same issue rather than
 * creating a second one — and the retry writes the issue's own fields again,
 * in case they were edited in between. Does NOT reload the issue list; callers decide how the
 * new issue reaches it.
 */
export async function createIssueWithAssociations(
  p: CreateIssueWithAssociationsParams
): Promise<number> {
  const issueId = await createFresh(p);
  await pmRepo.setIssueDisciplines(issueId, p.disciplineIds);
  const items = p.checklistItems ?? [];
  if (items.length > 0 && p.checklistSavedForRef?.current !== issueId) {
    await pmRepo.createChecklistItems(issueId, items, 0);
    if (p.checklistSavedForRef) p.checklistSavedForRef.current = issueId;
  }
  await pmRepo.setIssueAssignees(issueId, p.assigneeIds);
  // WHA-3347: link any docs the user staged in the modal. Best-effort
  // per-doc so a single failure doesn't lose the rest; sequential to
  // avoid hammering the junction-table inserts.
  for (const doc of p.stagedAttachments) {
    try {
      await pmRepo.addIssueDocument(issueId, doc.id);
    } catch (linkErr) {
      console.error("Failed to link staged attachment", doc, linkErr);
    }
  }
  return issueId;
}

async function createFresh(p: CreateIssueWithAssociationsParams): Promise<number> {
  if (!p.userProfileId || !p.orgId) {
    throw new Error("User or organisation not loaded");
  }
  let issueId = p.createdIssueIdRef.current;
  const parentIssueId = p.parentIssueId ?? (await bundleParentFor(p));
  const fields: Partial<PMIssue> = {
    title: p.title,
    status: p.status,
    start_date: p.startDate || null,
    due_date: p.dueDate || null,
    building_project_id: Number(p.projectId),
    // WHA-3331 / WHA-3389 — null when creating a top-level issue.
    parent_issue_id: parentIssueId,
  };
  if (issueId != null) {
    // A retry after a failed follow-up write: the form stayed editable, so the
    // fields may have changed since the insert. Written onto the existing row,
    // or the issue would keep what was typed the first time and the edits
    // would be lost without a word. The activity triggers record only real
    // changes, so an unchanged retry adds no history.
    await pmRepo.updateIssue(issueId, fields);
  } else {
    const created = await p.issuesStore.createIssue(
      {
        ...fields,
        building_id: p.buildingId,
        org_id: p.orgId,
        created_by: p.userProfileId,
      },
      p.prefilledDrawingAnchor
    );
    issueId = created.id;
    p.createdIssueIdRef.current = issueId;
    p.onIssueCreated?.(created);
    // Inside the guard: a retry after a failed follow-up write reuses the id
    // and must not count as a second issue.
    track("issue_created", {
      source: p.prefilledElement
        ? "ifc_element"
        : p.prefilledDrawingAnchor
          ? "drawing"
          : (p.analyticsSource ?? "dialog"),
      has_assignee: p.assigneeIds.length > 0,
      has_due_date: Boolean(p.dueDate),
      discipline_count: p.disciplineIds.length,
      attachment_count: p.stagedAttachments.length,
    });
  }
  if (p.prefilledElement) {
    await attachElement(p, issueId, p.prefilledElement);
  }
  return issueId;
}

/**
 * An issue raised from a viewer — pinned on a drawing, or on a model element —
 * joins the parent issue that bundles that document's comment threads, so the
 * Issues pages show one group per file. Only at creation, and only when the
 * form names no parent: an issue a person later detaches stays detached.
 * Best-effort — without the parent the issue is simply filed on its own.
 */
async function bundleParentFor(p: CreateIssueWithAssociationsParams): Promise<number | null> {
  const documentId = p.prefilledDrawingAnchor?.document_id ?? p.prefilledElement?.ifc_document_id;
  if (documentId == null) return null;
  try {
    return await pmRepo.ensureDocumentIssueRoot(documentId, Number(p.projectId));
  } catch (err) {
    console.error("Failed to resolve the document's parent issue", err);
    return null;
  }
}

/**
 * WHA-3521: attach the BIM element this issue was created from, and also link
 * its source .ifc document so it appears in Linked Documents. Element-link is
 * the source of truth; the doc link and the saved view are best-effort — the
 * issue itself already exists and must not be lost to either.
 */
async function attachElement(
  p: CreateIssueWithAssociationsParams,
  issueId: number,
  element: PrefilledElement
): Promise<void> {
  await pmRepo.addIssueElement(issueId, {
    anchor_id: element.anchor_id,
    element_guid: element.element_guid,
    element_name: element.element_name,
    element_type: element.element_type,
    ifc_document_id: element.ifc_document_id,
  });
  try {
    await pmRepo.addIssueDocument(issueId, element.ifc_document_id);
  } catch (docErr) {
    // already linked, or a transient failure — non-fatal.
    console.error("Failed to auto-link IFC document", docErr);
  }
  if (p.saveViewpoint && element.viewpoint) {
    try {
      await pmRepo.saveIssueViewpoint(issueId, element.viewpoint, p.userProfileId ?? null);
    } catch (vpErr) {
      // Unlike the document link above, this is an opt-in the user ticked and
      // was promised — the dialog closes on success, so a silent failure just
      // means the restore button never appears.
      console.error("Failed to save the issue viewpoint", vpErr);
      p.onViewpointSaveFailed(vpErr);
    }
  }
}
